import * as React from "react";

import { Theme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  TicketingRecurrenceType,
  getDatePlusNHours,
  getDatePlusNMonths,
  MAX_OCCURRENCE_LIMIT,
  MINIMUM_START_UTC_DATE_FOR_RECURRENCE,
} from "@simplyk/common";
import { UseFormReturn, useWatch } from "react-hook-form";

import { AuctionOpenDateField } from "./AuctionOpenDateField";
import { OpenAndCloseDateBundle } from "./OpenAndCloseDateBundle";
import { EditRecurrencesFormProps, TicketingRecurrenceData } from "./types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isRequired, isLaterThan, isOccurrenceLimitValid } from "@/common/helpers/validators";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardDateTimePicker } from "@/dashboard-design-system/DateTimePicker";
import { DateTimePickerProps } from "@/dashboard-design-system/DateTimePicker/types";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { Add } from "@/icons/outlined";

const REQUIRED_DATE_I18N_KEY = "ticketing.dateIsRequired";

export interface RecurrenceProps {
  form: UseFormReturn<EditRecurrencesFormProps>;
  recurrenceIndex: number;
  recurrence: TicketingRecurrenceData;
  isAlreadySaved: boolean;
  timezone: string;
  isAuction?: boolean;
  disabled?: boolean;
  isHidden?: boolean;
}

const SORTED_RECURRENCE_TYPES: TicketingRecurrenceType[] = Object.values(TicketingRecurrenceType);

export const TicketingRecurrence = React.forwardRef((props: RecurrenceProps, ref: React.Ref<HTMLDivElement>) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { form, recurrenceIndex, isAlreadySaved, isAuction, isHidden = false } = props;
  const hasEventSchedule = !isAuction;
  const { locale } = useLocaleContext();

  const { errors } = form.formState;

  const formRecurrenceError = errors.recurrences?.[recurrenceIndex];
  const startUtcError = formRecurrenceError?.startUtc;
  const endUtcError = formRecurrenceError?.endUtc;
  const repeatUntilDateError = formRecurrenceError?.repeatUntilDate;
  const recurrenceTypeField = `recurrences.${recurrenceIndex}.type` as const;
  const recurrenceIdField = `recurrences.${recurrenceIndex}.id` as const;
  const startUtcField = `recurrences.${recurrenceIndex}.startUtc` as const;
  const endUtcField = `recurrences.${recurrenceIndex}.endUtc` as const;
  const repeatUntilDateField = `recurrences.${recurrenceIndex}.repeatUntilDate` as const;
  const closeDateField = `recurrences.${recurrenceIndex}.closeDate` as const;
  const openDateField = `recurrences.${recurrenceIndex}.openDate` as const;

  const [isDisplayingOpenDate, setIsDisplayingOpenDate] = React.useState(false);
  const [isDisplayingCloseAndOpenBundle, setIsDisplayingCloseAndOpenBundle] = React.useState(false);

  const watchedRecurrenceIdField = useWatch({
    control: form.control,
    name: recurrenceIdField,
  });

  React.useEffect(() => {
    if (!watchedRecurrenceIdField) {
      form.setValue(recurrenceIdField, props.recurrence.id);
    }
  }, [props.recurrence.id, recurrenceIdField, form, watchedRecurrenceIdField]);

  const displayOpenDate = React.useCallback(() => {
    setIsDisplayingOpenDate(true);
  }, []);

  const hideOpenDate = React.useCallback(() => {
    setIsDisplayingOpenDate(false);
    form.setValue(openDateField, null, { shouldDirty: true });
  }, [form, openDateField]);

  const displayCloseAndOpenBundle = React.useCallback(() => {
    setIsDisplayingCloseAndOpenBundle(true);
  }, []);

  const hideCloseAndOpenBundle = React.useCallback(() => {
    setIsDisplayingCloseAndOpenBundle(false);
    form.setValue(closeDateField, null, { shouldDirty: true });
    form.setValue(openDateField, null, { shouldDirty: true });
  }, [form, closeDateField, openDateField]);

  const getTicketingRecurrenceTypeByLanguage = (type: TicketingRecurrenceType) => {
    return t("dashboard", `ticketing.recurrence.${type}`);
  };

  const startValue = useWatch({ control: form.control, name: startUtcField });
  const selectedTicketingRecurrenceType = useWatch({
    control: form.control,
    name: recurrenceTypeField,
  });
  const closeDate = useWatch({ control: form.control, name: closeDateField });
  const openDate = useWatch({ control: form.control, name: openDateField });
  const repeatUntilDate = useWatch({
    control: form.control,
    name: repeatUntilDateField,
  });

  const displayRepeatUntilDate = selectedTicketingRecurrenceType !== TicketingRecurrenceType.Once;
  const isEventDateDisabled = props.disabled || (!isAuction && isAlreadySaved && displayRepeatUntilDate);

  React.useEffect(() => {
    if (!displayRepeatUntilDate) {
      form.setValue(repeatUntilDateField, null, { shouldDirty: true });
    }
  }, [form, repeatUntilDateField, displayRepeatUntilDate]);

  React.useEffect(() => {
    if (isAuction) {
      if (openDate) {
        displayOpenDate();
      }
    } else if (closeDate || openDate) {
      displayCloseAndOpenBundle();
    }
  }, [closeDate, openDate, isAuction, displayOpenDate, displayCloseAndOpenBundle]);

  React.useEffect(() => {
    if (
      selectedTicketingRecurrenceType &&
      selectedTicketingRecurrenceType !== TicketingRecurrenceType.Once &&
      !repeatUntilDate
    ) {
      const repeatUntilDate = new Date();
      repeatUntilDate.setHours(23, 59, 0);
      form.setValue(repeatUntilDateField, getDatePlusNMonths(repeatUntilDate, 2), { shouldDirty: true });
    }
  }, [selectedTicketingRecurrenceType, form, repeatUntilDateField, repeatUntilDate]);

  const onStartDateChange: NonNullable<DateTimePickerProps<object>["onChange"]> = React.useCallback(
    (date) => {
      if (date) {
        const endDate = form.getValues(endUtcField);
        // Only set endUtc if it's not already set or if endUtc not valid anymore
        if (!endDate || new Date(endDate) <= new Date(date)) {
          form.setValue(endUtcField, getDatePlusNHours(date, 1));
        }

        const repeatUntilDate = form.getValues(repeatUntilDateField);
        // Only set repeatUntilDateField if it's not already set
        form.setValue(repeatUntilDateField, repeatUntilDate ? repeatUntilDate : getDatePlusNMonths(date, 1));
      }
    },
    [endUtcField, form, repeatUntilDateField]
  );

  React.useEffect(() => {
    if (startValue) {
      void form.trigger([endUtcField, repeatUntilDateField]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startValue, form]);

  const recurrenceTypeOnChange = React.useCallback(
    (event: SelectChangeEvent<unknown>) => {
      logAmplitudeEvent(AmplitudeEvents.FormEventRecurrenceChanged, {
        type: event.target.value,
      });
    },
    [logAmplitudeEvent]
  );

  return (
    <Grid container spacing={2} ref={ref} sx={isHidden ? { opacity: 0.4 } : undefined}>
      {hasEventSchedule && (
        <Grid item xs={12} md={6}>
          <DashboardDateTimePicker
            name={startUtcField}
            label={t("dashboard", "ticketing.recurrenceStartLabel")}
            control={form.control}
            locale={locale}
            size="small"
            rules={[isRequired(t("dashboard", REQUIRED_DATE_I18N_KEY))]}
            errorMessage={startUtcError?.message}
            disabled={isEventDateDisabled}
            data-test="ticketing-recurrence-start-date"
            onChange={onStartDateChange}
            timezone={props.timezone}
            minDate={MINIMUM_START_UTC_DATE_FOR_RECURRENCE}
          />
        </Grid>
      )}
      <Grid item xs={12} md={isAuction ? 12 : 6}>
        <DashboardDateTimePicker
          name={endUtcField}
          label={isAuction ? "" : t("dashboard", "ticketing.recurrenceEndLabel")}
          control={form.control}
          locale={locale}
          size="small"
          rules={[
            isRequired(t("dashboard", REQUIRED_DATE_I18N_KEY)),
            isLaterThan(
              startValue && new Date(startValue),
              t("dashboard", "ticketing.endUtcShouldBeLaterThanStartUtc")
            ),
          ]}
          errorMessage={endUtcError?.message}
          disabled={isEventDateDisabled}
          data-test="ticketing-recurrence-end-date"
          timezone={props.timezone}
          minDate={MINIMUM_START_UTC_DATE_FOR_RECURRENCE}
        />
      </Grid>
      {hasEventSchedule && (
        <Grid item xs={12} md={6}>
          <DashboardSelect
            label={t("dashboard", "ticketing.recurrenceTypeLabel")}
            disabled={props.disabled || isAlreadySaved}
            MenuProps={{
              anchorOrigin: {
                vertical: "center",
                horizontal: "center",
              },
              transformOrigin: {
                vertical: "center",
                horizontal: "center",
              },
            }}
            control={form.control}
            name={recurrenceTypeField}
            onChange={recurrenceTypeOnChange}
          >
            {SORTED_RECURRENCE_TYPES.map((menuItemTicketingRecurrenceType) => (
              <DashboardMenuItem key={menuItemTicketingRecurrenceType} value={menuItemTicketingRecurrenceType}>
                <div>
                  {getTicketingRecurrenceTypeByLanguage(menuItemTicketingRecurrenceType)}
                  {menuItemTicketingRecurrenceType === TicketingRecurrenceType.Monthly && (
                    <DashboardInformationTooltip
                      title={t("dashboard", "ticketing.recurrence.Monthly.info") as string}
                      alignOnText
                      placement="top"
                      sx={(theme: Theme) => ({
                        marginLeft: theme.spacing(1),
                      })}
                    />
                  )}
                </div>
              </DashboardMenuItem>
            ))}
          </DashboardSelect>
        </Grid>
      )}

      {hasEventSchedule && displayRepeatUntilDate && (
        <Grid item xs={12} md={6}>
          <DashboardDateTimePicker
            name={repeatUntilDateField}
            label={`${getTicketingRecurrenceTypeByLanguage(selectedTicketingRecurrenceType)} ${t(
              "dashboard",
              "ticketing.until"
            )}`}
            control={form.control}
            locale={locale}
            size="small"
            rules={[
              isRequired(t("dashboard", REQUIRED_DATE_I18N_KEY)),
              isLaterThan(
                startValue && new Date(startValue),
                t("dashboard", "ticketing.dateShouldBeLaterThanStartUtc")
              ),
              isOccurrenceLimitValid(
                t("dashboard", "ticketing.occurrenceLimitExceeded", {
                  count: MAX_OCCURRENCE_LIMIT,
                }),
                {
                  type: selectedTicketingRecurrenceType,
                  startUtc: startValue ? new Date(startValue) : null,
                }
              ),
            ]}
            errorMessage={repeatUntilDateError?.message}
            timezone={props.timezone}
          />
        </Grid>
      )}
      {isAuction && !isDisplayingOpenDate && (
        <Grid item xs={12}>
          <Grid container alignItems="center">
            <DashboardButton
              variant="outlined"
              vibe="neutral"
              onClick={displayOpenDate}
              startIcon={<Add />}
              size="small"
              data-test="ticketing-recurrence-display-open-date"
            >
              {t("dashboard", "ticketing.recurrenceLabelToDisplayOpenDate")}
            </DashboardButton>
          </Grid>
        </Grid>
      )}
      {isAuction && isDisplayingOpenDate && (
        <AuctionOpenDateField
          form={form}
          recurrenceIndex={recurrenceIndex}
          timezone={props.timezone}
          onRemove={hideOpenDate}
        />
      )}
      {hasEventSchedule && !isDisplayingCloseAndOpenBundle && (
        <Grid item xs={12}>
          <Grid container alignItems="center">
            <DashboardButton
              variant="outlined"
              vibe="neutral"
              onClick={displayCloseAndOpenBundle}
              startIcon={<Add />}
              size="small"
              data-test="ticketing-recurrence-display-open-and-close-date"
            >
              {t("dashboard", "ticketing.recurrenceLabelToDisplayOpenAndCloseDate")}
            </DashboardButton>
          </Grid>
        </Grid>
      )}
      {hasEventSchedule && isDisplayingCloseAndOpenBundle && (
        <OpenAndCloseDateBundle
          form={form}
          recurrenceIndex={recurrenceIndex}
          timezone={props.timezone}
          onRemove={hideCloseAndOpenBundle}
        />
      )}
    </Grid>
  );
});

TicketingRecurrence.displayName = "TicketingRecurrence";
